// The arb ladder as a VenueEngine: rests one order on the quoter leg and hedges fills on the hedger leg.

import type { Engine } from '../core/engine';
import type { FillRow } from '../core/db';
import { MC_SETTLE, MQ_PER_CONTRACT, floorContracts, type MC, type MQ } from '../core/money';
import type { FillEvent, LadderRow, UiOrder, UiSnapshot } from '../core/types';
import { logError, logInfo, logWarn } from '../core/log';
import { journal } from '../core/journal';
import type { ExploreEvent, ExploreMarket, ExploreState, RealVenueId, VenueEngine, VenueEvents, VenueHints } from '../venue/engine';

export interface ArbLeg {
  venue: RealVenueId;
  ticker: string;
}

export interface ArbLegs {
  q: ArbLeg;
  h: ArbLeg;
}

/** The pair's ticker: parseable, and never colliding with a real one. */
export function arbTicker(l: ArbLegs): string {
  return `${l.q.venue}:${l.q.ticker}|${l.h.venue}:${l.h.ticker}`;
}

export function parseArbTicker(t: string): ArbLegs | null {
  const m = t.match(/^(kalshi|polymarket):([^|]+)\|(kalshi|polymarket):(.+)$/);
  return m ? { q: { venue: m[1] as RealVenueId, ticker: m[2] }, h: { venue: m[3] as RealVenueId, ticker: m[4] } } : null;
}

type Listener<K extends keyof VenueEvents> = (payload: VenueEvents[K]) => void;
type Side = 'q' | 'h';
type ArbSide = 'bid' | 'ask';

interface Quote {
  orderId: string;
  priceMc: MC;
  sizeMq: MQ;
  /** The hedger level it was pegged to. */
  refMc: MC;
}

interface ArbOrder {
  id: string;
  pair: string;
  legs: ArbLegs;
  side: ArbSide;
  spreadMc: MC;
  qtyMq: MQ;
  accountId: string;
  filledMq: MQ;
  hedgedMq: MQ;
  unhedgedMq: MQ;
  /** The order resting on the quoter right now. */
  quote: Quote | null;
  /** Every quoter order id this arb order has used: fills are matched on these. */
  quoteIds: Set<string>;
  /** What each quote id was: price for a synthetic fill, when for the reconcile window. */
  quoteMeta: Map<string, { priceMc: MC; placedAt: number }>;
  /** Fractional contracts left over from hedges, carried until they make a whole one. */
  hedgeDustMq: MQ;
  /** The last placement the quoter refused, so it is not retried at the same level on every tick. */
  lastRefused: { priceMc: MC; refMc: MC; error: string } | null;
  status: 'quoting' | 'unhedged' | 'done' | 'cancelled';
  createdAt: number;
  /** Serialises every action on this order. */
  chain: Promise<void>;
  /** Fills handed to the chain and not hedged yet; while any wait, a re-peg stands down. */
  fillsQueued: number;
  /** Being cancelled or retired: nothing may put a new quote up. */
  closing: boolean;
}

interface Pair {
  legs: ArbLegs;
  refs: number;
  snaps: Record<Side, { msg: UiSnapshot; at: number } | null>;
}

/** Something the app must show, not just log: unhedged exposure, a failed cancel, a missed fill. */
export interface ArbAlarm {
  level: 'error' | 'warn';
  msg: string;
  /** The arb order it concerns, when one. */
  orderId?: string;
}

/** Contracts on a quoter leg that no hedge covers, per arb order. */
export interface ArbExposure {
  orderId: string;
  pair: string;
  leg: ArbLeg;
  side: ArbSide;
  unhedgedMq: MQ;
}

/** Hedge limit is the touch worsened by this many hedger ticks; doubled on the retry. */
const HEDGE_TOLERANCE_TICKS = 2;
/** A leg quiet for this long pulls the quote until it speaks again. */
const STALE_MS = 20_000;
/** A fill naming an order id no quote has yet is held this long for the ack that will. */
const PENDING_FILL_MS = 30_000;
/** After that, a held fill on a quoter leg is announced as unmatched. */
const PENDING_FILL_WARN_MS = 3_000;
/** The ack said the quote filled some: the fill stream gets this long to say so before it is synthesised. */
const ACK_FILL_GRACE_MS = 1_500;
/** Every quote is read back from its exchange this often; a fill count above what was seen is a missed fill. */
const RECONCILE_MS = 15_000;
/** A quote is still read back this long after it stopped resting: late fills land here. */
const RECONCILE_AFTER_MS = 180_000;
/** Retired orders remembered for late fills. */
const RETIRED_KEEP = 300;
/** The same alarm (order + kind) is not raised again within this window. */
const ALARM_REPEAT_MS = 30_000;
const PERSIST_KEY = 'kg_arb_quotes_v1';

/** Signed cents to one decimal. */
function cents(mc: MC | null): string {
  if (mc === null) return '—';
  const v = mc / 10;
  return `${v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}¢`;
}

export class ArbEngine implements VenueEngine {
  readonly venue = 'arb' as const;
  readonly label = 'Arb';
  readonly tradingEnabled = true;
  readonly hints: VenueHints = {
    explore: 'Set a quoter and a hedger above',
    ladder: 'Set a quoter and a hedger above',
    exploreEmpty: 'An arb ladder is built from its two legs above.',
  };

  private pairs = new Map<string, Pair>();
  private orders = new Map<string, ArbOrder>();
  private orderSeq = 0;
  private listeners = new Map<keyof VenueEvents, Set<Listener<any>>>();
  private phantomListeners = new Set<() => void>();
  private alarmListeners = new Set<(a: ArbAlarm) => void>();
  private alarmLast = new Map<string, number>();
  /** Orders that are done or cancelled, kept so a late fill on one of their quotes is still hedged. */
  private retired = new Map<string, ArbOrder>();
  /** Fills naming an order id no quote has claimed yet, keyed venue:orderId. */
  private pendingFills = new Map<string, { ev: FillEvent; at: number; warned: boolean }[]>();
  /** Per quote (venue:orderId): contracts accounted for, and how many were synthesised (may echo a real fill). */
  private quoteFills = new Map<string, { seenMq: MQ; syntheticMq: MQ }>();
  /** Quotes persisted by a previous page load, cancelled once their venue is up. */
  private strays = new Map<RealVenueId, { ticker: string; orderId: string }[]>();

  constructor(private engineFor: (v: RealVenueId) => VenueEngine, legEngines: VenueEngine[]) {
    for (const e of legEngines) {
      e.on('snapshot', (msg) => this.onLeg(e.venue as RealVenueId, msg));
      e.on('fill', (ev) => this.onFill(e.venue as RealVenueId, ev));
    }
    this.loadStrays();
    // Staleness is checked on a clock because a silent leg sends nothing to react to.
    // globalThis: the engine is also constructed in unit tests, where there is no window.
    globalThis.setInterval(() => this.checkStale(), 5_000);
    globalThis.setInterval(() => this.sweepPendingFills(), 1_000);
    globalThis.setInterval(() => void this.reconcile(), RECONCILE_MS);
  }

  /** Alarms are what the app must put in front of the user; the log gets them too. */
  onAlarm(fn: (a: ArbAlarm) => void): () => void {
    this.alarmListeners.add(fn);
    return () => { this.alarmListeners.delete(fn); };
  }

  private alarm(level: ArbAlarm['level'], msg: string, orderId?: string, kind = ''): void {
    (level === 'error' ? logError : logWarn)('arb', msg);
    const key = `${orderId ?? ''}/${kind || msg}`;
    const last = this.alarmLast.get(key) ?? 0;
    if (Date.now() - last < ALARM_REPEAT_MS) return;
    this.alarmLast.set(key, Date.now());
    for (const fn of this.alarmListeners) {
      try {
        fn({ level, msg, orderId });
      } catch (e) {
        console.error('[arb] alarm listener error', e);
      }
    }
  }

  /** Every unhedged contract on a quoter leg right now, by arb order. */
  exposure(): ArbExposure[] {
    return [...this.orders.values()]
      .filter((o) => o.unhedgedMq > 0)
      .map((o) => ({ orderId: o.id, pair: o.pair, leg: o.legs.q, side: o.side, unhedgedMq: o.unhedgedMq }));
  }

  on<K extends keyof VenueEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  private emit<K extends keyof VenueEvents>(event: K, payload: VenueEvents[K]): void {
    for (const fn of this.listeners.get(event) ?? []) {
      try {
        fn(payload);
      } catch (e) {
        console.error('[arb] listener error', e);
      }
    }
  }

  subscribe(ticker: string): void {
    const cur = this.pairs.get(ticker);
    if (cur) {
      cur.refs++;
      this.emit('snapshot', this.buildUiSnapshot(ticker));
      return;
    }
    const legs = parseArbTicker(ticker);
    if (!legs) return;
    this.pairs.set(ticker, { legs, refs: 1, snaps: { q: null, h: null } });
    this.engineFor(legs.q.venue).subscribe(legs.q.ticker);
    this.engineFor(legs.h.venue).subscribe(legs.h.ticker);
    this.emit('snapshot', this.buildUiSnapshot(ticker));
  }

  releaseTicker(ticker: string): number {
    const cur = this.pairs.get(ticker);
    if (!cur) return 0;
    if (--cur.refs > 0) return cur.refs;
    this.pairs.delete(ticker);
    this.engineFor(cur.legs.q.venue).releaseTicker(cur.legs.q.ticker);
    this.engineFor(cur.legs.h.venue).releaseTicker(cur.legs.h.ticker);
    return 0;
  }

  private onLeg(venue: RealVenueId, msg: UiSnapshot): void {
    this.cancelStrays(venue);
    for (const [ticker, pair] of this.pairs) {
      let hit = false;
      for (const side of ['q', 'h'] as Side[]) {
        const leg = pair.legs[side];
        if (leg.venue === venue && leg.ticker === msg.ticker) {
          pair.snaps[side] = { msg, at: Date.now() };
          hit = true;
        }
      }
      if (hit) this.emit('snapshot', this.buildUiSnapshot(ticker));
    }
    this.onLegForOrders(venue, msg);
  }

  /** ms since each leg last ticked; null before its first snapshot. */
  legAges(ticker: string): Record<Side, number | null> {
    const p = this.pairs.get(ticker);
    const age = (s: Side) => (p?.snaps[s] ? Date.now() - p.snaps[s]!.at : null);
    return { q: age('q'), h: age('h') };
  }

  buildUiSnapshot(ticker: string): UiSnapshot {
    const pair = this.pairs.get(ticker);
    const q = pair?.snaps.q?.msg ?? null;
    const h = pair?.snaps.h?.msg ?? null;
    // The finer of the two legs' grids: every leg price is a multiple of it.
    const tick = q && h ? Math.min(q.stats.tickMc, h.stats.tickMc) : 10;
    const hi = MC_SETTLE - tick;
    const lo = -hi;

    type Lvl = { priceMc: MC; mq: MQ };
    let ask: Lvl | null = null;
    let bid: Lvl | null = null;
    let crossAsk: Lvl | null = null;
    let crossBid: Lvl | null = null;
    if (q && h) {
      const askQ = q.stats.bestYesAsk;
      const bidQ = q.stats.bestYesBid;
      const askH = h.stats.bestYesAsk;
      const bidH = h.stats.bestYesBid;
      if (askQ !== null && askH !== null) ask = { priceMc: askQ - askH, mq: Math.min(sizeAt(q, askQ, 'ask'), sizeAt(h, askH, 'ask')) };
      if (bidQ !== null && bidH !== null) bid = { priceMc: bidQ - bidH, mq: Math.min(sizeAt(q, bidQ, 'bid'), sizeAt(h, bidH, 'bid')) };
      if (askQ !== null && bidH !== null) crossAsk = { priceMc: askQ - bidH, mq: Math.min(sizeAt(q, askQ, 'ask'), sizeAt(h, bidH, 'bid')) };
      if (bidQ !== null && askH !== null) crossBid = { priceMc: bidQ - askH, mq: Math.min(sizeAt(q, bidQ, 'bid'), sizeAt(h, askH, 'ask')) };
    }

    const mine = [...this.orders.values()].filter((o) => o.pair === ticker && (o.status === 'quoting' || o.status === 'unhedged'));
    const rows: LadderRow[] = [];
    if (q && h) {
      for (let p = hi; p >= lo; p -= tick) {
        const at = mine.filter((o) => o.spreadMc === p && o.status === 'quoting');
        const myMq = at.reduce((s, o) => s + (o.qtyMq - o.filledMq), 0);
        // Arb bests carry size in their own column; cross prices in the opposite one.
        const bidMq = (bid?.priceMc === p ? bid.mq : 0) + (crossAsk?.priceMc === p ? crossAsk.mq : 0);
        const askMq = (ask?.priceMc === p ? ask.mq : 0) + (crossBid?.priceMc === p ? crossBid.mq : 0);
        const side = !at.length ? '' : at.some((o) => o.quote) ? (at[0].side === 'bid' ? 'y' : 'n') : 'p';
        rows.push([myMq, bidMq, p, askMq, null, at.length, side]);
      }
    }

    const orders: UiOrder[] = mine.map((o) => ({
      order_id: o.id, client_order_id: '', ticker, status: o.status === 'unhedged' ? 'partially_filled' : 'resting',
      side: o.side === 'bid' ? 'yes' : 'no',
      price_mc: o.side === 'bid' ? o.spreadMc : MC_SETTLE - o.spreadMc,
      remaining_mq: o.qtyMq - o.filledMq, queue_position: null, created_time: new Date(o.createdAt).toISOString(),
      source: 'manual', account_id: o.accountId,
    }));
    const unhedged = mine.reduce((s, o) => s + o.unhedgedMq, 0);

    return {
      type: 'snapshot',
      ticker,
      stats: {
        bestYesBid: bid?.priceMc ?? null,
        bestYesAsk: ask?.priceMc ?? null,
        midYesDeci: bid && ask ? (bid.priceMc + ask.priceMc) * 5 : null,
        openOrders: mine.length,
        bidOrders: mine.filter((o) => o.side === 'bid').length,
        askOrders: mine.filter((o) => o.side === 'ask').length,
        volume: 0,
        // Position on the pair: hedged pairs held; unhedged shows as P&L-less exposure in the log.
        positionMq: mine.reduce((s, o) => s + (o.side === 'bid' ? o.hedgedMq : -o.hedgedMq), 0),
        pnlCash: null,
        tickMc: tick,
        priceHiMc: hi,
        priceLoMc: lo,
        // Arb bid above arb ask just means the hedger is wider than the quoter: never warn.
        crossed: false,
        marketStatus: unhedged ? 'unhedged' : 'open',
        marketResult: '',
        // Already folded into the rows above; a ladder that hides crossing takes them back out.
        arbCross: { buyMc: crossAsk?.priceMc ?? null, buyMq: crossAsk?.mq ?? 0, sellMc: crossBid?.priceMc ?? null, sellMq: crossBid?.mq ?? 0 },
      },
      rows,
      orders,
      fills: {},
      brackets: {},
      pnlByAccount: {},
    };
  }

  private run(o: ArbOrder, fn: () => Promise<void>): Promise<void> {
    const next = o.chain.then(fn).catch((e) => logError('arb', `${o.id}: ${(e as Error).message}`));
    o.chain = next;
    return next;
  }

  // The click arrives in Kalshi terms (side bought, its price); an ask click is a NO buy at settle − S.
  placeOrder: Engine['placeOrder'] = async (args) => {
    const pair = this.pairs.get(args.ticker);
    if (!pair) return { ok: false, error: 'arb ladder has no legs' };
    if (!pair.snaps.q || !pair.snaps.h) return { ok: false, error: 'both legs must be live before quoting' };
    const side: ArbSide = args.side === 'yes' ? 'bid' : 'ask';
    const spreadMc = args.side === 'yes' ? args.priceMc : MC_SETTLE - args.priceMc;
    const id = `arb-${Date.now().toString(36)}-${++this.orderSeq}`;
    const o: ArbOrder = {
      id, pair: args.ticker, legs: pair.legs, side, spreadMc, qtyMq: args.qtyMq, accountId: args.accountId,
      filledMq: 0, hedgedMq: 0, unhedgedMq: 0, quote: null, quoteIds: new Set(), quoteMeta: new Map(), hedgeDustMq: 0, lastRefused: null,
      status: 'quoting', createdAt: Date.now(), chain: Promise.resolve(), fillsQueued: 0, closing: false,
    };

    const legsText = `quoter ${o.legs.q.venue}:${o.legs.q.ticker} / hedger ${o.legs.h.venue}:${o.legs.h.ticker}`;
    if (o.legs.q.venue === o.legs.h.venue && o.legs.q.ticker === o.legs.h.ticker) {
      logWarn('arb', `${id}: both legs are the same market (${legsText}) — this hedges nothing`);
    }
    logInfo('arb', `${id} ${args.mode === 'fok' ? 'cross' : 'quote'} ${side} ${args.qtyMq / MQ_PER_CONTRACT} @ ${cents(spreadMc)} — ${legsText}`);
    journal({
      venue: 'arb', kind: 'arb', tag: id, ticker: o.pair,
      msg: `${args.mode === 'fok' ? 'CROSS' : 'QUOTE'} ${side} ${args.qtyMq / MQ_PER_CONTRACT} @ ${cents(spreadMc)} — ${legsText} · acct ${o.accountId}`,
      data: { args, legs: o.legs, quoter: pair.snaps.q.msg.stats, hedger: pair.snaps.h.msg.stats },
    });
    if (args.mode === 'fok') return this.cross(o);

    this.orders.set(id, o);
    this.emit('snapshot', this.buildUiSnapshot(o.pair));
    await this.run(o, () => this.syncQuote(o, 'placed'));
    if (o.status === 'cancelled') return { ok: false, error: 'quote could not be placed' };
    if (!o.quote && o.lastRefused && o.status === 'quoting') {
      // A refusal of the first quote is the click's own outcome; a refusal on a later re-peg just retries later.
      const why = o.lastRefused.error;
      this.retire(o, 'cancelled', `refused at placement: ${why}`);
      return { ok: false, error: `${o.legs.q.venue} refused the quote: ${why}` };
    }
    return { ok: true, order: { order_id: id, client_order_id: null, status: 'resting', remaining_mq: args.qtyMq, fill_mq: 0, yes_price_mc: spreadMc, raw: {} } };
  };

  cancelOrder: Engine['cancelOrder'] = async (orderId) => {
    const o = this.orders.get(orderId);
    if (!o) return { ok: false, error: `no arb order ${orderId}` };
    let failed: string | null = null;
    await this.run(o, async () => {
      o.closing = true;
      const unhedgedBefore = o.unhedgedMq;
      if (o.quote) {
        const ok = await this.cancelQuote(o);
        if (!ok) {
          o.closing = false;
          failed = 'quoter cancel failed — quote may still be resting';
          return;
        }
      }
      // The quote had filled before the cancel reached it and that hedge failed: the row stays, with its exposure.
      if (o.unhedgedMq > unhedgedBefore) {
        failed = `the quote filled before the cancel and the hedge failed — UNHEDGED ${o.unhedgedMq / MQ_PER_CONTRACT} on ${o.legs.q.ticker}, flatten by hand`;
        return;
      }
      // A fill the cancel turned up may have completed the order already.
      if (this.orders.has(o.id) && o.status !== 'done') this.retire(o, 'cancelled', 'cancelled');
    });
    return failed ? { ok: false, error: failed } : { ok: true };
  };

  cancelLevel: Engine['cancelLevel'] = async (ticker, yesPriceMc, accountId) => {
    const ids = [...this.orders.values()]
      .filter((o) => o.pair === ticker && o.spreadMc === yesPriceMc && (!accountId || o.accountId === accountId))
      .map((o) => o.id);
    return this.cancelMany(ids);
  };

  cancelAllOrders: Engine['cancelAllOrders'] = async (ticker, accountId) => {
    const ids = [...this.orders.values()]
      .filter((o) => o.pair === ticker && (!accountId || o.accountId === accountId))
      .map((o) => o.id);
    return this.cancelMany(ids);
  };

  private async cancelMany(ids: string[]): Promise<{ ok: boolean; cancelled: number; failed: number; error?: string }> {
    const results = await Promise.all(ids.map((id) => this.cancelOrder(id)));
    const failed = results.filter((r) => !r.ok).length;
    const error = results.find((r) => !r.ok)?.error;
    return { ok: failed === 0, cancelled: ids.length - failed, failed, ...(error ? { error } : {}) };
  }

  /** Every resting quote, fire-and-forget: the page is going away. */
  cancelAllQuotes(): void {
    for (const o of this.orders.values()) {
      if (o.quote) void this.engineFor(o.legs.q.venue).cancelOrder(o.quote.orderId);
    }
  }

  armBracket: Engine['armBracket'] = async () => ({ ok: false, error: 'brackets do not apply to an arb ladder' });

  // The peg: an arb order at spread S rests at S + the hedger's same-side price, capped by that side's depth.
  private reference(o: ArbOrder): { refMc: MC; depthMq: MQ; tickH: number } | null {
    const h = this.pairs.get(o.pair)?.snaps.h?.msg;
    if (!h) return null;
    const refMc = o.side === 'ask' ? h.stats.bestYesAsk : h.stats.bestYesBid;
    if (refMc === null) return null;
    return { refMc, depthMq: sizeAt(h, refMc, o.side === 'ask' ? 'ask' : 'bid'), tickH: h.stats.tickMc };
  }

  /** What the quoter order should be right now; null when nothing should rest. */
  private desired(o: ArbOrder): { priceMc: MC; sizeMq: MQ; refMc: MC } | null {
    const q = this.pairs.get(o.pair)?.snaps.q?.msg;
    const ref = this.reference(o);
    if (!q || !ref) return null;
    const tick = q.stats.tickMc;
    const raw = o.spreadMc + ref.refMc;
    // Round to the quoter's grid in the safe direction: a bid down, an ask up.
    const priceMc = o.side === 'bid' ? Math.floor(raw / tick) * tick : Math.ceil(raw / tick) * tick;
    if (priceMc < tick || priceMc > MC_SETTLE - tick) return null;
    const sizeMq = Math.max(0, Math.min(o.qtyMq - o.filledMq, ref.depthMq));
    if (sizeMq <= 0) return null;
    return { priceMc, sizeMq, refMc: ref.refMc };
  }

  /** Bring the resting quoter order in line with `desired`: cancel, then place. Never two at once. */
  private async syncQuote(o: ArbOrder, why: string): Promise<void> {
    // A fill waiting in the chain must go first: a re-peg ahead of it would size the new quote as if nothing filled.
    if (o.status !== 'quoting' || o.closing || o.fillsQueued > 0) return;
    let want = this.desired(o);
    const have = o.quote;
    if (want && have && have.priceMc === want.priceMc && have.sizeMq === want.sizeMq) {
      if (have.refMc !== want.refMc) have.refMc = want.refMc;
      return;
    }
    if (want && o.lastRefused && o.lastRefused.priceMc === want.priceMc && o.lastRefused.refMc === want.refMc) return;
    if (have) {
      const filledBefore = o.filledMq;
      const ok = await this.cancelQuote(o);
      if (!ok) return; // still resting; try again on the next tick
      // A fill during the cancel makes the size computed above stale; stop here and let the hedge re-sync.
      if (o.filledMq !== filledBefore || o.fillsQueued > 0 || o.status !== 'quoting' || o.closing) return;
      // The hedger may have moved while the cancel was out.
      want = this.desired(o);
    }
    if (!want) {
      const ref = this.reference(o);
      const detail = !this.pairs.get(o.pair)?.snaps.q ? 'no quoter book' : !ref ? 'no hedger touch' : ref.depthMq <= 0 ? 'no size at the hedger touch' : 'price off the grid';
      if (have) logInfo('arb', `${o.id} ${why}: quote pulled (nothing to rest — ${detail})`);
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, msg: `${why}: nothing to rest — ${detail}${have ? ' (quote pulled)' : ''}`, data: { ref, filledMq: o.filledMq } });
      this.phantomsChanged();
      return;
    }
    const eng = this.engineFor(o.legs.q.venue);
    const res = await eng.placeOrder({
      ticker: o.legs.q.ticker,
      side: o.side === 'bid' ? 'yes' : 'no',
      priceMc: o.side === 'bid' ? want.priceMc : MC_SETTLE - want.priceMc,
      qtyMq: want.sizeMq,
      mode: 'post',
      accountId: o.accountId,
      tag: `${o.id}/q`,
    });
    // Kalshi answers a post-only that would cross with a 200 and a cancelled order, not an error — a refusal too.
    if (!res.ok || !res.order.order_id || res.order.status === 'canceled') {
      const error = res.ok ? (res.order.order_id ? 'cancelled on arrival (would cross)' : 'no order id') : res.error;
      o.lastRefused = { priceMc: want.priceMc, refMc: want.refMc, error };
      logWarn('arb', `${o.id} ${why}: quoter refused ${o.side} ${want.sizeMq / MQ_PER_CONTRACT} @ ${cents(want.priceMc)} on ${o.legs.q.ticker} — ${error}`);
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: true, msg: `${why}: ${o.legs.q.venue} REFUSED ${o.side} ${want.sizeMq / MQ_PER_CONTRACT} @ ${cents(want.priceMc)} on ${o.legs.q.ticker} — ${error}`, data: { want, result: res } });
      this.phantomsChanged();
      return;
    }
    o.lastRefused = null;
    const quoteId = res.order.order_id;
    o.quote = { orderId: quoteId, priceMc: want.priceMc, sizeMq: res.order.remaining_mq ?? want.sizeMq, refMc: want.refMc };
    o.quoteIds.add(quoteId);
    o.quoteMeta.set(quoteId, { priceMc: want.priceMc, placedAt: Date.now() });
    this.deadman(o.legs.q.venue, quoteId, o.legs.q.ticker, true);
    this.persist();
    logInfo('arb', `${o.id} ${why}: quote ${o.side} ${want.sizeMq / MQ_PER_CONTRACT} @ ${cents(want.priceMc)} on ${o.legs.q.ticker} `
      + `(row ${cents(o.spreadMc)} + hedger ${o.side} ${cents(want.refMc)})${have ? `, was ${cents(have.priceMc)}` : ''} · ${res.order.order_id}`);
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: res.order.order_id, msg: `${why}: quote ${o.side} ${want.sizeMq / MQ_PER_CONTRACT} @ ${cents(want.priceMc)} on ${o.legs.q.venue}:${o.legs.q.ticker} (row ${cents(o.spreadMc)} + hedger ${cents(want.refMc)})${have ? `, was ${cents(have.priceMc)}` : ''} · ${res.order.order_id}`, data: { want, was: have, result: res } });
    this.phantomsChanged();
    this.emit('snapshot', this.buildUiSnapshot(o.pair));

    // Polymarket can report an immediate fill inside the placement response itself; hedge any held fills now.
    for (const held of this.takePendingFills(o.legs.q.venue, quoteId)) await this.hedge(o, held.ev);
    // The ack's fill count is exchange truth; unreported-by-grace-period is hedged straight from it.
    const ackFilled = res.order.fill_mq ?? (res.order.remaining_mq !== null ? Math.max(0, want.sizeMq - res.order.remaining_mq) : 0);
    if (ackFilled > 0) {
      globalThis.setTimeout(() => void this.run(o, () => this.settleQuoteFills(o, quoteId, ackFilled, 'placement ack')), ACK_FILL_GRACE_MS);
    }
  }

  // Any exchange-reported fill above what the stream delivered is hedged from here; a later echo isn't hedged twice.
  private async settleQuoteFills(o: ArbOrder, quoteId: string, filledMq: MQ, source: string, beforeCancel = false): Promise<void> {
    const key = `${o.legs.q.venue}:${quoteId}`;
    const seen = this.quoteFills.get(key)?.seenMq ?? 0;
    const missed = filledMq - seen;
    if (missed <= 0) return;
    const meta = o.quoteMeta.get(quoteId);
    if (!meta) return;
    if (beforeCancel) {
      // Not a missed fill — the stream's event is usually still in flight; hedged here so it goes first.
      logWarn('arb', `${o.id}: quote ${quoteId} on ${o.legs.q.ticker} had filled ${filledMq / MQ_PER_CONTRACT} before the cancel reached it — hedging the ${missed / MQ_PER_CONTRACT} now`);
    } else {
      this.alarm('error', `${o.id}: MISSED FILL — ${source} says ${filledMq / MQ_PER_CONTRACT} filled on quote ${quoteId} (${o.legs.q.ticker}), fill stream reported ${seen / MQ_PER_CONTRACT}. Hedging the ${missed / MQ_PER_CONTRACT} now.`, o.id, 'missed');
    }
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: quoteId, bad: !beforeCancel, msg: `${beforeCancel ? 'FILLED BEFORE CANCEL' : 'MISSED FILL'}: ${source} says ${filledMq / MQ_PER_CONTRACT} filled on ${o.legs.q.venue}:${o.legs.q.ticker} quote ${quoteId}, stream reported ${seen / MQ_PER_CONTRACT} — synthesising ${missed / MQ_PER_CONTRACT}`, data: { filledMq, seen, missed, source } });
    await this.hedge(o, {
      type: 'fill_event', ticker: o.legs.q.ticker, account_id: o.accountId, ts: Math.floor(Date.now() / 1000),
      side: o.side === 'bid' ? 'yes' : 'no', mq: missed, yes_price_mc: meta.priceMc, is_taker: false, order_id: quoteId,
    }, true);
  }

  /** True when the quote is gone from the venue, whether we cancelled it or it had already filled. */
  private async cancelQuote(o: ArbOrder): Promise<boolean> {
    const q = o.quote;
    if (!q) return true;
    const res = await this.engineFor(o.legs.q.venue).cancelOrder(q.orderId, `${o.id}/q`);
    if (!res.ok) {
      this.alarm('error', `${o.id}: CANCEL FAILED for quote ${q.orderId} on ${o.legs.q.ticker} — ${res.error}. The quote may still be resting.`, o.id, 'cancel');
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: q.orderId, bad: true, msg: `cancel of quote ${q.orderId} FAILED — ${res.error}`, data: { quote: q, result: res } });
      return false;
    }
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: q.orderId, msg: `quote ${q.orderId} ${res.filledMq !== undefined ? `was already closed — exchange says ${res.filledMq / MQ_PER_CONTRACT} filled` : 'cancelled'} (${cents(q.priceMc)} × ${q.sizeMq / MQ_PER_CONTRACT})`, data: { quote: q, result: res } });
    o.quote = null;
    this.deadman(o.legs.q.venue, q.orderId, o.legs.q.ticker, false);
    this.persist();
    // A cancel that found the quote already closed reports the exchange's fill count; hedge it now, before re-quoting.
    if (res.filledMq !== undefined) await this.settleQuoteFills(o, q.orderId, res.filledMq, `${o.legs.q.venue} cancel read-back`, true);
    return true;
  }

  private onLegForOrders(venue: RealVenueId, msg: UiSnapshot): void {
    for (const o of this.orders.values()) {
      if (o.status !== 'quoting') continue;
      const isH = o.legs.h.venue === venue && o.legs.h.ticker === msg.ticker;
      const isQ = o.legs.q.venue === venue && o.legs.q.ticker === msg.ticker;
      if (!isH && !isQ) continue;
      const want = this.desired(o);
      const have = o.quote;
      const same = (!want && !have) || (!!want && !!have && want.priceMc === have.priceMc && want.sizeMq === have.sizeMq);
      // A refused level that has not moved is not retried on every frame.
      const refusedSame = !!want && !!o.lastRefused && o.lastRefused.priceMc === want.priceMc && o.lastRefused.refMc === want.refMc;
      if (!same && !refusedSame) void this.run(o, () => this.syncQuote(o, isH ? 'hedger moved' : 'quoter moved'));
    }
  }

  private onFill(venue: RealVenueId, ev: FillEvent): void {
    if (!ev.order_id) return;
    const o = this.orderForQuote(venue, ev.order_id);
    if (o) {
      o.fillsQueued++;
      void this.run(o, () => {
        o.fillsQueued--;
        return this.hedge(o, ev);
      });
      return;
    }
    // No quote has this id yet; the ack is usually still in flight and will claim it, else the sweep announces it.
    const key = `${venue}:${ev.order_id}`;
    const list = this.pendingFills.get(key) ?? [];
    list.push({ ev, at: Date.now(), warned: false });
    this.pendingFills.set(key, list);
  }

  /** The order, live or retired, that used this quote id. */
  private orderForQuote(venue: RealVenueId, orderId: string): ArbOrder | null {
    for (const map of [this.orders, this.retired]) {
      for (const o of map.values()) if (o.legs.q.venue === venue && o.quoteIds.has(orderId)) return o;
    }
    return null;
  }

  /** Fills held for this quote id, removed from the hold. */
  private takePendingFills(venue: RealVenueId, orderId: string): { ev: FillEvent; at: number }[] {
    const key = `${venue}:${orderId}`;
    const list = this.pendingFills.get(key) ?? [];
    this.pendingFills.delete(key);
    return list;
  }

  /** Held fills on a quoter leg that no ack has claimed: announce, then forget. */
  private sweepPendingFills(): void {
    const now = Date.now();
    for (const [key, list] of this.pendingFills) {
      const [venue, orderId] = key.split(':', 2) as [RealVenueId, string];
      const kept = list.filter((f) => now - f.at < PENDING_FILL_MS);
      for (const f of kept) {
        if (f.warned || now - f.at < PENDING_FILL_WARN_MS) continue;
        f.warned = true;
        const onLeg = [...this.pairs.values()].some((p) => p.legs.q.venue === venue && p.legs.q.ticker === f.ev.ticker);
        if (!onLeg) continue;
        this.alarm('warn', `fill of ${f.ev.mq / MQ_PER_CONTRACT} @ ${cents(f.ev.yes_price_mc)} on ${venue}:${f.ev.ticker} (order ${orderId}) matches no arb quote — if it was one, it is unhedged`, undefined, `unmatched/${orderId}`);
        journal({ venue: 'arb', kind: 'arb', ticker: f.ev.ticker, orderId, bad: true, msg: `unmatched fill on quoter leg ${venue}:${f.ev.ticker}: ${f.ev.mq / MQ_PER_CONTRACT} @ ${cents(f.ev.yes_price_mc)} on order ${orderId}`, data: { fill: f.ev } });
      }
      if (kept.length) this.pendingFills.set(key, kept);
      else this.pendingFills.delete(key);
    }
  }

  // Catches a fill the stream never sent: every resting or recently-resting quote is read back and reconciled.
  private async reconcile(): Promise<void> {
    const now = Date.now();
    const jobs: { o: ArbOrder; quoteId: string }[] = [];
    for (const map of [this.orders, this.retired]) {
      for (const o of map.values()) {
        for (const [quoteId, meta] of o.quoteMeta) {
          const live = o.quote?.orderId === quoteId;
          if (live || now - meta.placedAt < RECONCILE_AFTER_MS) jobs.push({ o, quoteId });
        }
      }
    }
    for (const { o, quoteId } of jobs) {
      const eng = this.engineFor(o.legs.q.venue);
      if (!eng.orderFill) continue;
      let truth: { filledMq: MQ; status: string } | null;
      try {
        truth = await eng.orderFill(quoteId, o.legs.q.ticker);
      } catch (e) {
        logWarn('arb', `${o.id}: could not read quote ${quoteId} back from ${o.legs.q.venue} — ${(e as Error).message}`);
        continue;
      }
      if (!truth) continue;
      const seen = this.quoteFills.get(`${o.legs.q.venue}:${quoteId}`)?.seenMq ?? 0;
      if (truth.filledMq > seen) await this.run(o, () => this.settleQuoteFills(o, quoteId, truth!.filledMq, `${o.legs.q.venue} read-back`));
    }
  }

  // FOK on the hedger at the touch worsened by a tolerance, doubled once on retry; two misses leave it unhedged.
  private async hedge(o: ArbOrder, ev: FillEvent, synthetic = false): Promise<void> {
    // A synthetic fill may be echoed later by the real event; that echo is consumed here, not re-hedged.
    const key = `${o.legs.q.venue}:${ev.order_id}`;
    const acct = this.quoteFills.get(key) ?? { seenMq: 0, syntheticMq: 0 };
    this.quoteFills.set(key, acct);
    let mq = ev.mq;
    if (!synthetic && acct.syntheticMq > 0) {
      const echo = Math.min(mq, acct.syntheticMq);
      acct.syntheticMq -= echo;
      mq -= echo;
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: ev.order_id, msg: `fill of ${ev.mq / MQ_PER_CONTRACT} on quote ${ev.order_id}: ${echo / MQ_PER_CONTRACT} already hedged from the exchange's count`, data: { fill: ev, echo } });
      if (mq <= 0) {
        // Nothing left to hedge, but a re-peg may have stood down for this fill.
        await this.syncQuote(o, 'fill already hedged');
        return;
      }
    }
    acct.seenMq += mq;
    if (synthetic) acct.syntheticMq += mq;
    const late = o.status === 'done' || o.status === 'cancelled';
    if (late) logWarn('arb', `${o.id}: LATE FILL of ${mq / MQ_PER_CONTRACT} on ${o.legs.q.ticker} after the order was ${o.status} — hedging`);
    o.filledMq += mq;
    if (o.quote && o.quote.orderId === ev.order_id) {
      o.quote.sizeMq -= mq;
      if (o.quote.sizeMq <= 0) {
        this.deadman(o.legs.q.venue, o.quote.orderId, o.legs.q.ticker, false);
        o.quote = null;
        this.persist();
      }
    }
    logInfo('arb', `${o.id} FILLED ${mq / MQ_PER_CONTRACT} @ ${cents(ev.yes_price_mc)} on ${o.legs.q.ticker} — hedging`);
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: ev.order_id, msg: `quote FILLED ${mq / MQ_PER_CONTRACT} @ ${cents(ev.yes_price_mc)} on ${o.legs.q.venue}:${o.legs.q.ticker}${synthetic ? ' (from the exchange\'s count)' : ''} — hedging on ${o.legs.h.venue}:${o.legs.h.ticker}`, data: { fill: ev, synthetic, late, filledMq: o.filledMq, hedger: this.pairs.get(o.pair)?.snaps.h?.msg.stats } });

    // The hedger takes whole contracts only; a fractional fill's remainder carries to the next one.
    o.hedgeDustMq += mq;
    const hedgeMq = floorContracts(o.hedgeDustMq);
    o.hedgeDustMq -= hedgeMq;
    if (hedgeMq <= 0) {
      logInfo('arb', `${o.id}: ${mq / MQ_PER_CONTRACT} is under a contract — carried (${o.hedgeDustMq / MQ_PER_CONTRACT} waiting)`);
      await this.syncQuote(o, 'partial fill');
      return;
    }

    const h = this.pairs.get(o.pair)?.snaps.h?.msg;
    const eng = this.engineFor(o.legs.h.venue);
    let hedged = false;
    let lastErr = 'no hedger book';
    if (h) {
      const tickH = h.stats.tickMc;
      for (const mult of [1, 2]) {
        const tol = HEDGE_TOLERANCE_TICKS * tickH * mult;
        const touch = o.side === 'ask' ? h.stats.bestYesAsk : h.stats.bestYesBid;
        if (touch === null) { lastErr = 'hedger has no touch'; break; }
        // ask quote filled → we sold the quoter → BUY the hedger (lift, up to touch + tol)
        // bid quote filled → we bought the quoter → SELL the hedger (hit, down to touch − tol), sent as a NO buy
        const limitMc = o.side === 'ask' ? Math.min(MC_SETTLE - tickH, touch + tol) : Math.max(tickH, touch - tol);
        const res = await eng.placeOrder({
          ticker: o.legs.h.ticker,
          side: o.side === 'ask' ? 'yes' : 'no',
          priceMc: o.side === 'ask' ? limitMc : MC_SETTLE - limitMc,
          qtyMq: hedgeMq,
          mode: 'fok',
          accountId: o.accountId,
          tag: `${o.id}/h`,
        });
        const got = res.ok ? (res.order.fill_mq ?? hedgeMq) : 0;
        journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: !(res.ok && res.order.status !== 'canceled' && got >= hedgeMq), msg: `hedge attempt ${mult}: ${o.side === 'ask' ? 'buy' : 'sell'} ${hedgeMq / MQ_PER_CONTRACT} on ${o.legs.h.venue}:${o.legs.h.ticker} limit ${cents(limitMc)} (touch ${cents(touch)}) → ${res.ok ? `${res.order.status}, filled ${got / MQ_PER_CONTRACT}` : `REFUSED: ${res.error}`}`, data: { limitMc, touch, tol, result: res } });
        if (res.ok && res.order.status !== 'canceled' && got >= hedgeMq) {
          hedged = true;
          const hedgeMc = res.order.yes_price_mc ?? limitMc;
          const net = o.side === 'ask' ? ev.yes_price_mc - hedgeMc : ev.yes_price_mc - hedgeMc;
          logInfo('arb', `${o.id} HEDGED ${hedgeMq / MQ_PER_CONTRACT} on ${o.legs.h.ticker} @ ${cents(hedgeMc)} → net ${cents(net)} (row ${cents(o.spreadMc)})`);
          break;
        }
        lastErr = res.ok ? `${res.order.status}, filled ${got / MQ_PER_CONTRACT}` : res.error;
        logWarn('arb', `${o.id}: hedge attempt ${mult} at ${cents(limitMc)} missed — ${lastErr}`);
      }
    }
    if (hedged) {
      o.hedgedMq += hedgeMq;
      if (late) logInfo('arb', `${o.id}: late fill hedged`);
    } else {
      o.unhedgedMq += hedgeMq;
      o.status = 'unhedged';
      // A retired order with exposure comes back into view: the banner and the row need it.
      this.retired.delete(o.id);
      this.orders.set(o.id, o);
      this.alarm('error', `${o.id}: UNHEDGED ${o.unhedgedMq / MQ_PER_CONTRACT} on ${o.legs.q.ticker} — hedge failed (${lastErr}). Quote pulled; flatten the ${o.legs.q.ticker} leg by hand, then cancel the arb row.`, o.id, 'unhedged');
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: true, msg: `UNHEDGED ${o.unhedgedMq / MQ_PER_CONTRACT} on ${o.legs.q.venue}:${o.legs.q.ticker} — ${lastErr}`, data: { unhedgedMq: o.unhedgedMq, hedgedMq: o.hedgedMq } });
      if (o.quote) await this.cancelQuote(o);
      this.phantomsChanged();
      this.emit('snapshot', this.buildUiSnapshot(o.pair));
      return;
    }
    if (late) {
      this.emit('snapshot', this.buildUiSnapshot(o.pair));
      return;
    }
    if (o.filledMq >= o.qtyMq) {
      // A re-peg between the fill and this hedge can leave a quote resting; its cancel hedges any late fill.
      if (o.quote) {
        o.closing = true;
        await this.cancelQuote(o);
      }
      // Not if that nested hedge already retired the order, or failed and left it unhedged.
      if (o.status === 'quoting') this.retire(o, 'done', `complete: ${o.hedgedMq / MQ_PER_CONTRACT} pairs`);
      return;
    }
    await this.syncQuote(o, 'partial fill');
    this.emit('snapshot', this.buildUiSnapshot(o.pair));
  }

  /** Take both legs now with fill-or-kill; if only one leg fills, reverse it at once. */
  private async cross(o: ArbOrder): Promise<ReturnType<Engine['placeOrder']>> {
    const p = this.pairs.get(o.pair);
    const q = p?.snaps.q?.msg.stats;
    const h = p?.snaps.h?.msg.stats;
    if (!q || !h) return { ok: false, error: 'both legs must be live to cross' };
    const askQ = q.bestYesAsk; const bidQ = q.bestYesBid; const askH = h.bestYesAsk; const bidH = h.bestYesBid;
    const buyPair = o.side === 'bid';
    const qPx = buyPair ? askQ : bidQ;
    const hPx = buyPair ? bidH : askH;
    if (qPx === null || hPx === null) return { ok: false, error: 'no two-sided touch to cross' };
    const eff = qPx - hPx;
    logInfo('arb', `${o.id} CROSS ${o.side} ${o.qtyMq / MQ_PER_CONTRACT} @ ${cents(eff)}: ${buyPair ? 'lift' : 'hit'} ${o.legs.q.ticker} @ ${cents(qPx)}, ${buyPair ? 'hit' : 'lift'} ${o.legs.h.ticker} @ ${cents(hPx)}`);
    const legQ = this.engineFor(o.legs.q.venue);
    const legH = this.engineFor(o.legs.h.venue);
    const [rq, rh] = await Promise.all([
      legQ.placeOrder({ ticker: o.legs.q.ticker, side: buyPair ? 'yes' : 'no', priceMc: buyPair ? qPx : MC_SETTLE - qPx, qtyMq: o.qtyMq, mode: 'fok', accountId: o.accountId, tag: `${o.id}/q` }),
      legH.placeOrder({ ticker: o.legs.h.ticker, side: buyPair ? 'no' : 'yes', priceMc: buyPair ? MC_SETTLE - hPx : hPx, qtyMq: o.qtyMq, mode: 'fok', accountId: o.accountId, tag: `${o.id}/h` }),
    ]);
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: !(rq.ok && rh.ok), msg: `CROSS ${o.side} ${o.qtyMq / MQ_PER_CONTRACT} @ ${cents(eff)}: quoter → ${rq.ok ? `${rq.order.status} filled ${(rq.order.fill_mq ?? 0) / MQ_PER_CONTRACT}` : `REFUSED: ${rq.error}`}; hedger → ${rh.ok ? `${rh.order.status} filled ${(rh.order.fill_mq ?? 0) / MQ_PER_CONTRACT}` : `REFUSED: ${rh.error}`}`, data: { qPx, hPx, eff, quoter: rq, hedger: rh } });
    const filled = (r: typeof rq) => r.ok && r.order.status !== 'canceled' && (r.order.fill_mq ?? o.qtyMq) >= o.qtyMq;
    const fq = filled(rq);
    const fh = filled(rh);
    if (fq && fh) {
      logInfo('arb', `${o.id} CROSSED ${o.qtyMq / MQ_PER_CONTRACT} pairs @ ${cents(eff)}`);
      return { ok: true, order: { order_id: o.id, client_order_id: null, status: 'executed', remaining_mq: 0, fill_mq: o.qtyMq, yes_price_mc: eff, raw: {} } };
    }
    if (!fq && !fh) return { ok: false, error: `neither leg filled (${rq.ok ? rq.order.status : rq.error}; ${rh.ok ? rh.order.status : rh.error})` };
    // One leg is on: reverse it now, at its current touch.
    const legged = fq ? o.legs.q : o.legs.h;
    const eng = fq ? legQ : legH;
    const st = fq ? q : h;
    const boughtYes = fq ? buyPair : !buyPair;
    const back = boughtYes ? st.bestYesBid : st.bestYesAsk;
    this.alarm('error', `${o.id}: ONE-LEGGED — ${legged.ticker} filled, ${(fq ? o.legs.h : o.legs.q).ticker} did not (${fq ? (rh.ok ? rh.order.status : rh.error) : (rq.ok ? rq.order.status : rq.error)}). Reversing ${legged.ticker}.`);
    if (back === null) {
      this.alarm('error', `${o.id}: one-legged on ${legged.ticker} and no touch to reverse into — flatten by hand`, o.id, 'one-legged');
      return { ok: false, error: `one-legged on ${legged.ticker} and no touch to reverse into — flatten by hand` };
    }
    const rv = await eng.placeOrder({ ticker: legged.ticker, side: boughtYes ? 'no' : 'yes', priceMc: boughtYes ? MC_SETTLE - back : back, qtyMq: o.qtyMq, mode: 'fok', accountId: o.accountId, tag: `${o.id}/${fq ? 'q' : 'h'}` });
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: !filled(rv), msg: `ONE-LEGGED on ${legged.venue}:${legged.ticker} — reverse at ${cents(back)} → ${rv.ok ? `${rv.order.status} filled ${(rv.order.fill_mq ?? 0) / MQ_PER_CONTRACT}` : `REFUSED: ${rv.error}`}`, data: { legged, back, result: rv } });
    if (filled(rv)) return { ok: false, error: `one leg filled and was reversed at ${cents(back)}; nothing crossed` };
    this.alarm('error', `${o.id}: ONE-LEGGED on ${legged.ticker}: reverse failed (${rv.ok ? rv.order.status : rv.error}) — flatten by hand`, o.id, 'one-legged');
    return { ok: false, error: `ONE-LEGGED on ${legged.ticker}: reverse failed (${rv.ok ? rv.order.status : rv.error}) — flatten by hand` };
  }

  private retire(o: ArbOrder, status: ArbOrder['status'], why: string): void {
    o.status = status;
    o.closing = true;
    if (o.quote) {
      // Still here means a prior cancel failed; retry, keeping its id in quoteIds so a late fill is still hedged.
      const q = o.quote;
      logWarn('arb', `${o.id}: retiring with quote ${q.orderId} still resting on ${o.legs.q.ticker} — cancelling it`);
      journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, orderId: q.orderId, bad: true, msg: `retiring (${status}) with quote ${q.orderId} still resting — cancelling`, data: { quote: q } });
      void this.engineFor(o.legs.q.venue).cancelOrder(q.orderId, `${o.id}/q`).then((res) => {
        if (res.ok) this.deadman(o.legs.q.venue, q.orderId, o.legs.q.ticker, false);
        else this.alarm('error', `${o.id}: quote ${q.orderId} on ${o.legs.q.ticker} is STILL RESTING after the arb order ${status === 'done' ? 'completed' : 'was cancelled'} — cancel failed (${res.error}). Cancel it on ${o.legs.q.venue} by hand.`, o.id, 'orphan');
      });
      o.quote = null;
    }
    this.orders.delete(o.id);
    // Kept: a fill on one of its quotes can still arrive, and must still be hedged.
    if (o.quoteIds.size) {
      this.retired.set(o.id, o);
      while (this.retired.size > RETIRED_KEEP) this.retired.delete(this.retired.keys().next().value!);
    }
    this.persist();
    logInfo('arb', `${o.id} ${why}`);
    journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, msg: `${status}: ${why}`, data: { filledMq: o.filledMq, hedgedMq: o.hedgedMq, unhedgedMq: o.unhedgedMq } });
    this.phantomsChanged();
    this.emit('snapshot', this.buildUiSnapshot(o.pair));
  }

  /** A leg that has gone quiet cannot be trusted: pull the quote until it speaks again. */
  private checkStale(): void {
    for (const o of this.orders.values()) {
      if (o.status !== 'quoting' || !o.quote) continue;
      const ages = this.legAges(o.pair);
      const stale = (['q', 'h'] as Side[]).some((s) => ages[s] === null || ages[s]! > STALE_MS);
      if (stale) {
        logWarn('arb', `${o.id}: a leg is stale — pulling the quote`);
        journal({ venue: 'arb', kind: 'arb', tag: o.id, ticker: o.pair, bad: true, msg: `a leg is stale (q ${ages.q === null ? 'never' : `${Math.round(ages.q / 1000)}s`}, h ${ages.h === null ? 'never' : `${Math.round(ages.h / 1000)}s`}) — pulling the quote`, data: { ages, quote: o.quote } });
        void this.run(o, async () => { if (o.quote) await this.cancelQuote(o); this.phantomsChanged(); });
      }
    }
  }

  /** Kalshi's deadman cancels registered orders if this tab dies; Polymarket has none yet. */
  private deadman(venue: RealVenueId, orderId: string, ticker: string, add: boolean): void {
    const eng = this.engineFor(venue) as VenueEngine & { noteDeadmanOrder?: (id: string, t: string) => void; forgetDeadmanOrder?: (id: string) => void };
    if (add) eng.noteDeadmanOrder?.(orderId, ticker);
    else eng.forgetDeadmanOrder?.(orderId);
  }

  /** Resting quotes survive a reload only as something to cancel: a fresh page adopts nothing. */
  private persist(): void {
    try {
      const rows = [...this.orders.values()].filter((o) => o.quote).map((o) => ({ venue: o.legs.q.venue, ticker: o.legs.q.ticker, orderId: o.quote!.orderId }));
      localStorage.setItem(PERSIST_KEY, JSON.stringify(rows));
    } catch { /* storage blocked */ }
  }

  private loadStrays(): void {
    try {
      const rows = JSON.parse(localStorage.getItem(PERSIST_KEY) || '[]') as { venue: RealVenueId; ticker: string; orderId: string }[];
      for (const r of rows) {
        if (!r?.orderId || !r.ticker || !r.venue) continue;
        const list = this.strays.get(r.venue) ?? [];
        list.push({ ticker: r.ticker, orderId: r.orderId });
        this.strays.set(r.venue, list);
      }
      if (rows.length) logWarn('arb', `${rows.length} arb quote(s) were resting when the page last closed — cancelling as each venue comes up`);
    } catch { /* nothing persisted */ }
  }

  private cancelStrays(venue: RealVenueId): void {
    const list = this.strays.get(venue);
    if (!list) return;
    this.strays.delete(venue);
    const eng = this.engineFor(venue);
    for (const s of list) {
      eng.noteOrder?.(s.orderId, s.ticker);
      void eng.cancelOrder(s.orderId).then((r) => {
        if (r.ok) logInfo('arb', `stray quote ${s.orderId} on ${s.ticker} cancelled`);
        else logError('arb', `stray quote ${s.orderId} on ${s.ticker} could not be cancelled — ${r.error}`);
      });
    }
    if (!this.strays.size) this.persist();
  }

  /** Called when a quote is placed, moves or goes; the app repaints leg ladders. */
  onPhantomChange(fn: () => void): () => void {
    this.phantomListeners.add(fn);
    return () => { this.phantomListeners.delete(fn); };
  }

  private phantomsChanged(): void {
    for (const fn of this.phantomListeners) fn();
  }

  // A phantom row keeps an arb order's intent visible on the leg ladder when its quote isn't currently resting.
  phantomsFor(venue: RealVenueId, ticker: string, accountId: string): { priceMc: MC; mq: MQ }[] {
    const out: { priceMc: MC; mq: MQ }[] = [];
    for (const o of this.orders.values()) {
      if (o.status !== 'quoting' || o.quote || o.accountId !== accountId) continue;
      if (o.legs.q.venue !== venue || o.legs.q.ticker !== ticker) continue;
      const want = this.desired(o);
      if (want) out.push({ priceMc: want.priceMc, mq: want.sizeMq });
    }
    return out;
  }

  createAccount: Engine['createAccount'] = (name) => this.engineFor('kalshi').createAccount(name);
  recentFills: Engine['recentFills'] = async (): Promise<FillRow[]> => [];
  tradeHistory: Engine['tradeHistory'] = async () => ({ trades: [], exhausted: true, hitCap: false });
  priceBars: Engine['priceBars'] = async () => [];
  bidAskHistory: Engine['bidAskHistory'] = async () => [];
  volumeProfile: Engine['volumeProfile'] = async () => ({ byPrice: new Map<MC, MQ>(), trades: 0, complete: true });
  searchMarkets: Engine['searchMarkets'] = async () => [];
  exploreState(): ExploreState { return { building: false, count: null }; }
  async searchEvents(): Promise<{ hits: ExploreEvent[]; total: number }> { return { hits: [], total: 0 }; }
  async eventMarkets(): Promise<ExploreMarket[]> { return []; }
}

/** Bid or ask size resting at one YES level, from the snapshot's rows. */
function sizeAt(s: UiSnapshot, priceMc: number, side: 'bid' | 'ask'): number {
  const r = s.rows.find((x) => x[2] === priceMc);
  return r ? (side === 'bid' ? r[1] : r[3]) : 0;
}
